<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { StepperRootEmits, StepperRootProps } from 'reka-ui'
import { reactiveOmit } from '@vueuse/core'
import { StepperRoot, useForwardPropsEmits } from 'reka-ui'
import { cn } from '@ds/lib/utils'

/*
 * Reka's Stepper. `modelValue` is the 1-based step number; `linear` (default
 * true) is what gates the unreached steps, so a rail where every step is
 * reachable passes `:linear="false"`.
 *
 * What it provides that a row of buttons does not: arrow-key movement between
 * triggers, `data-state` per item, `aria-labelledby`/`aria-describedby` wired
 * from StepperTitle/StepperDescription, and a polite live region announcing
 * "Step n of m" on every change. It does NOT set `aria-current` — pass that on
 * the trigger yourself.
 */
const props = defineProps<StepperRootProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<StepperRootEmits>()

const delegatedProps = reactiveOmit(props, 'class')

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <StepperRoot
    v-slot="slotProps"
    v-bind="forwarded"
    :class="cn(
      'flex gap-2',
      // (derived) The registry assumes horizontal; a vertical rail should not
      // have to re-declare its own direction.
      'data-[orientation=vertical]:flex-col',
      props.class,
    )"
  >
    <slot v-bind="slotProps" />
  </StepperRoot>
</template>
